'use client'

import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import IngredientCheckerInstallAsk from '@/app/components/IngredientCheckerInstallAsk'
import { track } from '@/utils/analytics'
import { INGREDIENTS as SHARED_INGREDIENTS, SEVERITY_ORDER as SHARED_SEVERITY_ORDER, type IngredientFlag, type IngredientSeverity } from '../microplastic-ingredients/_data/ingredients'

// ─────────────────────────────────────────────────────────────────────────────
// Flagged ingredient database — imported from the shared module at
// /microplastic-ingredients/_data/ingredients. Single source of truth shared
// with the /microplastic-ingredients/[slug] programmatic SEO pages.
//
// Severity levels:
//  - high:   solid plastic polymer or known microbead (e.g. polyethylene, PMMA)
//  - medium: liquid polymer / siloxane / strong chemical of concern in plastics
//  - low:    grey-area emulsifier / minor PEG variant — flagged but contextualised
// ─────────────────────────────────────────────────────────────────────────────
type Severity = IngredientSeverity
type Flag = IngredientFlag

// FLAGS comes from the shared module so any ingredient added there auto-
// appears in the scanner AND gets a programmatic /microplastic-ingredients/[slug]
// page. Single edit point for the whole database.
const FLAGS: Flag[] = SHARED_INGREDIENTS

// Order severities for sorting / display. Imported from the shared module
// to keep ordering consistent across the scanner and the index page.
const SEVERITY_ORDER = SHARED_SEVERITY_ORDER

interface ScanResult {
  flag: Flag
  /** The raw token from the input that matched. Helps the user see *what* in
   *  their ingredient list triggered the flag. */
  matchedToken: string
}

/**
 * Tokenise the pasted ingredient list. Cosmetics labels separate ingredients
 * with commas, dots, semicolons, and occasionally parentheses or newlines.
 * Lower-case everything for matching.
 */
function tokenise(input: string): string[] {
  return input
    .toLowerCase()
    .split(/[,;.\n\r()[\]{}*•·]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
}

/**
 * Scan the ingredient text and return one result per flagged ingredient.
 * Deduplicates so the same polymer isn't reported twice if it appears in
 * multiple guises (e.g. "polyethylene" plus "polyethylene glycol-100").
 */
function scanIngredients(input: string): ScanResult[] {
  const tokens = tokenise(input)
  const matched = new Map<string, ScanResult>()

  for (const token of tokens) {
    for (const flag of FLAGS) {
      const hit = flag.terms.find((term) => token.includes(term))
      if (hit) {
        if (!matched.has(flag.name)) {
          matched.set(flag.name, { flag, matchedToken: token })
        }
        break // don't double-count one token across multiple flags
      }
    }
  }

  // Array.from rather than [...spread] because tsconfig targets ES5 — the
  // spread of a Map iterator needs --downlevelIteration which the project
  // doesn't enable.
  return Array.from(matched.values()).sort(
    (a, b) => SEVERITY_ORDER[a.flag.severity] - SEVERITY_ORDER[b.flag.severity]
  )
}

const SEVERITY_STYLES: Record<Severity, { badge: string; label: string }> = {
  high: { badge: 'bg-red-100 text-red-700 border-red-200', label: 'High concern' },
  medium: { badge: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Medium concern' },
  low: { badge: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Low concern' },
  // Unreachable here by design: the checker only ever sees INGREDIENTS, and
  // nothing in that array has severity 'none'. Present to satisfy the Record.
  none: { badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Not a microplastic' },
}

// Example ingredient list — single tap fills the textarea so the user can see
// the tool work without typing. Sourced from a typical mid-shelf moisturiser
// label, lightly altered.
const EXAMPLE_INGREDIENTS =
  'Water, Glycerin, Dimethicone, Cyclopentasiloxane, Cetyl Alcohol, Polyethylene, PEG-100 Stearate, Carbomer, Acrylates/C10-30 Alkyl Acrylate Crosspolymer, Phenoxyethanol, Methylparaben, Fragrance.'
const MAX_INPUT_LENGTH = 20_000

/**
 * Session-scoped "fire this event at most once" guard.
 *
 * WHY THIS EXISTS (2026-08-19). `ingredient_checker_start` and
 * `ingredient_checker_first_result` were both documented as firing once per
 * session, but were guarded only by component state (`hasStarted`,
 * `scanCount === 1`). React state dies with the mount, so a reader who
 * navigated away and came back re-fired both. GA4 for the 30 days to 19 Aug
 * 2026 measured the damage:
 *
 *   ingredient_checker_start          144 events / 102 sessions = 1.41x
 *   ingredient_checker_first_result    95 events /  71 sessions = 1.34x
 *   risk_checker_complete              23 events /  23 sessions = 1.00x  <- correct
 *
 * So funnel entry was overstated by ~40% and every conversion rate computed
 * off it was understated by the same. `risk_checker_complete` is the reference:
 * a correctly-guarded once-per-session event lands on exactly 1.00.
 *
 * sessionStorage rather than state because it survives remounts, and rather
 * than localStorage because the intent is per-visit, not forever. It matches
 * the existing `mp_*` session keys used by StickyMobileAppCTA and EventPopup.
 *
 * KNOWN IMPRECISION, deliberate: a browser sessionStorage session is the tab's
 * lifetime, while a GA4 session also ends after 30 minutes idle. A reader idle
 * for 31 minutes in the same tab starts a new GA4 session but will NOT re-fire
 * here. That biases very slightly toward under-counting, which is the safer
 * direction — the bug being fixed was a 40% over-count.
 *
 * NOT applied to `ingredient_checker_complete`: that one fires per scan on
 * purpose (it is the repeat-use volume metric) and is correct as-is.
 */
const SESSION_ONCE_KEYS = {
  start: 'mp_ic_started',
  firstResult: 'mp_ic_first_result',
} as const

function claimSessionOnce(key: string): boolean {
  try {
    if (sessionStorage.getItem(key) === '1') return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    // Private-mode Safari throws on sessionStorage. Fall back to firing so we
    // never silently lose the event; worst case is the old behaviour.
    return true
  }
}

export default function IngredientChecker() {
  const [input, setInput] = useState('')
  const [hasScanned, setHasScanned] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState('')
  // How many products this reader has checked in this session. GA4 says the
  // median ingredient-checker user runs ~9 scans (194 completes / 21 users),
  // pasting a fresh label each time — which is precisely the manual labour the
  // app removes. Counting the scans lets the install ask escalate from "here's
  // a tool" to "you have now done this by hand nine times", which is a far
  // stronger and more honest argument.
  const [scanCount, setScanCount] = useState(0)

  // Recompute results on every input change once the user has triggered the
  // first scan. useMemo keeps it cheap on subsequent renders.
  const results = useMemo(() => (hasScanned ? scanIngredients(input) : []), [input, hasScanned])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    if (scanError) setScanError('')
    if (!hasStarted && e.target.value.length > 0) {
      // hasStarted still short-circuits within this mount so we do not touch
      // sessionStorage on every keystroke; claimSessionOnce is what actually
      // makes the event once-per-session across remounts.
      setHasStarted(true)
      if (claimSessionOnce(SESSION_ONCE_KEYS.start)) {
        track('ingredient_checker_start', {
          cta_surface: 'ingredient_checker',
          page_type: 'tool_ingredient_checker',
        })
      }
    }
  }

  const handleScan = () => {
    if (isScanning) return
    const trimmedInput = input.trim()
    if (trimmedInput.length === 0) {
      setScanError('Paste an ingredient list before scanning.')
      track('validation_error', {
        cta_surface: 'ingredient_checker',
        page_type: 'tool_ingredient_checker',
        validation_field: 'ingredient_list',
        validation_reason: 'empty',
      })
      return
    }
    if (trimmedInput.length > MAX_INPUT_LENGTH) {
      setScanError('That list is too long. Shorten it to 20,000 characters and try again.')
      track('validation_error', {
        cta_surface: 'ingredient_checker',
        page_type: 'tool_ingredient_checker',
        validation_field: 'ingredient_list',
        validation_reason: 'too_long',
      })
      return
    }

    setIsScanning(true)
    setScanError('')
    track('scan_started', {
      cta_surface: 'ingredient_checker',
      page_type: 'tool_ingredient_checker',
      input_character_count: trimmedInput.length,
      ingredient_token_count: tokenise(trimmedInput).length,
      scan_count: scanCount + 1,
    })

    // Yield once so React can paint the immediate "Scanning…" state before
    // parsing. This removes the dead-click feel observed in replay while
    // keeping the matching algorithm and result presentation unchanged.
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        try {
          const resultCount = scanIngredients(trimmedInput).length
          setHasScanned(true)
          const nextScanCount = scanCount + 1
          setScanCount(nextScanCount)

          // 2026-08-03. These two events are NOT a funnel and were never comparable:
    // `ingredient_checker_start` fires ONCE per session (see claimSessionOnce
    // above — it was only once per MOUNT until 2026-08-19, which overstated it
    // by ~40%), while `ingredient_checker_complete` fires on EVERY scan. That is
    // why GA4 shows 89 starts against 284 completes — not a double-fire bug,
    // just two different units of measurement wearing funnel-shaped names. Any
    // Explore funnel built on them reports a >100% completion rate and is
    // meaningless.
    //
    // We deliberately do NOT rename `ingredient_checker_complete`: it is a
    // registered KEY event and renaming would sever the historical series.
    // Instead we add a once-per-session sibling so a real funnel exists:
    //   ingredient_checker_start -> ingredient_checker_first_result -> app_store_click
    // and `ingredient_checker_complete` keeps its per-scan meaning as the
    // repeat-use/volume metric it actually is.
    // nextScanCount === 1 is per-MOUNT, which is why this over-fired; the
    // session guard is what makes it genuinely once per visit. scanCount itself
    // is deliberately left per-mount because the UI uses it to escalate the
    // install ask, and that is a within-session-view behaviour.
          if (nextScanCount === 1 && claimSessionOnce(SESSION_ONCE_KEYS.firstResult)) {
            track('ingredient_checker_first_result', {
              cta_surface: 'ingredient_checker',
              page_type: 'tool_ingredient_checker',
              result_count: resultCount,
            })
          }

          // KEY event in GA4 — per-scan volume signal (one per product checked).
          track('ingredient_checker_complete', {
            cta_surface: 'ingredient_checker',
            page_type: 'tool_ingredient_checker',
            result_count: resultCount,
            scan_count: nextScanCount,
          })
          track('scan_success', {
            cta_surface: 'ingredient_checker',
            page_type: 'tool_ingredient_checker',
            result_count: resultCount,
            scan_count: nextScanCount,
          })
        } catch {
          setHasScanned(false)
          setScanError('The scan could not finish. Please try again.')
          track('scan_failed', {
            cta_surface: 'ingredient_checker',
            page_type: 'tool_ingredient_checker',
            failure_stage: 'ingredient_matching',
          })
        } finally {
          setIsScanning(false)
        }
      }, 0)
    })
  }

  const handleExample = () => {
    setInput(EXAMPLE_INGREDIENTS)
    if (!hasStarted) {
      setHasStarted(true)
      if (claimSessionOnce(SESSION_ONCE_KEYS.start)) {
        track('ingredient_checker_start', {
          cta_surface: 'ingredient_checker_example',
          page_type: 'tool_ingredient_checker',
        })
      }
    }
  }

  // Clearing the form is how a reader moves on to their NEXT product, so this
  // is the repeat-use path, not an exit. scanCount deliberately survives —
  // it's a session-level count of products checked, and resetting it here
  // would erase exactly the signal the install ask is built on.
  const handleReset = () => {
    setInput('')
    setHasScanned(false)
    setScanError('')
  }

  // Group results by category for a clean breakdown in the UI.
  const groupedResults = useMemo(() => {
    const groups: Record<string, ScanResult[]> = {}
    for (const result of results) {
      ;(groups[result.flag.category] ??= []).push(result)
    }
    return groups
  }, [results])

  const highCount = results.filter((r) => r.flag.severity === 'high').length
  const mediumCount = results.filter((r) => r.flag.severity === 'medium').length

  return (
    <div className="min-h-screen bg-white">
      <div className="content pt-6">
        <Breadcrumbs items={[{ label: 'Microplastic Ingredient Checker' }]} />
      </div>

      {/* HERO */}
      <section className="content pt-6 pb-8 md:pt-8 md:pb-10">
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
          Free tool · No signup
        </p>
        <h1 className="font-display text-[clamp(1.875rem,5vw,3rem)] font-bold text-ink mb-4 leading-[1.08]">
          Microplastic Ingredient Checker
        </h1>
        <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl">
          Paste any cosmetic, skincare, or personal-care ingredient list and we&apos;ll flag
          every plastic polymer, <strong>polyethylene</strong>, <strong>PEG</strong>,{' '}
          <strong>acrylates copolymer</strong>, <strong>carbomer</strong>,{' '}
          <strong>dimethicone</strong>, <strong>polyurethane</strong>, and 20+ more.
        </p>
      </section>

      {/* INPUT */}
      <section className="content pb-4">
        <div className="bg-cream rounded-2xl border border-ink/10 p-5 md:p-6">
          <label htmlFor="ingredient-input" className="block text-sm font-semibold text-gray-800 mb-2">
            Ingredient list
          </label>
          <textarea
            id="ingredient-input"
            value={input}
            onChange={handleInputChange}
            maxLength={MAX_INPUT_LENGTH}
            disabled={isScanning}
            placeholder="Water, Glycerin, Dimethicone, Cetyl Alcohol, Polyethylene, PEG-100 Stearate, Carbomer..."
            rows={6}
            className="w-full px-4 py-3 text-base border border-ink/10 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition resize-y leading-relaxed"
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleScan}
              data-analytics-action="ingredient_scan"
              disabled={input.trim().length === 0 || isScanning}
              aria-busy={isScanning}
              className="inline-flex items-center gap-2 bg-ink text-cream font-semibold px-5 py-2.5 min-h-[44px] rounded-control hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors pressable"
            >
              {isScanning ? 'Scanning…' : 'Scan ingredients'}
            </button>
            {!hasScanned && (
              <button
                type="button"
                onClick={handleExample}
                data-analytics-action="ingredient_example"
                disabled={isScanning}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-forest underline px-2 py-2"
              >
                Try an example
              </button>
            )}
            {hasScanned && (
              <button
                type="button"
                onClick={handleReset}
                data-analytics-action="ingredient_clear"
                disabled={isScanning}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-forest underline px-2 py-2"
              >
                Clear
              </button>
            )}
          </div>
          {scanError && (
            <p className="mt-3 text-sm text-red-600" role="alert" aria-live="assertive">
              {scanError}
            </p>
          )}
        </div>
      </section>

      {/* RESULTS */}
      {hasScanned && (
        <section className="content py-6" aria-live="polite">
          {/* Summary card */}
          <div className="relative overflow-hidden border-t-2 border-ink pt-6 pb-2 mb-5">
            {results.length === 0 ? (
              <div className="flex items-start gap-3">
                <FaCheckCircle className="w-6 h-6 text-green-500 shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h2 className="text-xl font-bold text-ink mb-1">
                    No reference-list names matched
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    The pasted text did not match the polymer, silicone, PEG,
                    acrylate, or plasticiser names in our reference list. This
                    does not prove absence, purity, or safety: labels can be
                    incomplete, synonyms vary, and the tool performs no lab test.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-1" aria-hidden="true" />
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-ink mb-1">
                    {results.length} reference-list match{results.length === 1 ? '' : 'es'}
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {highCount > 0 && (
                      <>
                        <strong>{highCount} higher-priority</strong>
                        {mediumCount > 0 ? `, ${mediumCount} medium-priority.` : '.'}
                      </>
                    )}
                    {highCount === 0 && mediumCount > 0 && (
                      <>
                        <strong>{mediumCount} medium-priority</strong>. No higher-priority hard-plastic polymer names matched.
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* The ask, at the top of the results — not buried under them.
              This is the instant of peak intent: the reader has just been told
              what's in their product and hasn't yet scrolled. The old CTA sat
              below every flagged-ingredient card, which on a 7-flag scan put it
              several screens away from this moment. */}
          <IngredientCheckerInstallAsk
            resultCount={results.length}
            highCount={highCount}
            scanCount={scanCount}
            placement="inline"
          />

          {/* Grouped results by category */}
          {Object.entries(groupedResults).map(([category, items]) => (
            <div key={category} className="mb-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {category}
              </h3>
              <ul className="space-y-3">
                {items.map((result) => {
                  const styles = SEVERITY_STYLES[result.flag.severity]
                  return (
                    <li
                      key={result.flag.name}
                      className="border-t border-ink/10 py-5"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="text-base font-bold text-ink">{result.flag.name}</h4>
                        <span
                          className={`shrink-0 text-2xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${styles.badge}`}
                        >
                          {styles.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">
                        {result.flag.explainer}
                      </p>
                      <p className="text-xs text-gray-500 italic">
                        Matched in your list:{' '}
                        <span className="not-italic font-mono text-gray-700">
                          {result.matchedToken}
                        </span>
                      </p>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}

          {/* The closing ask. Same result-aware copy engine as the inline one,
              but with room to breathe, a reader who has read every flagged
              ingredient card is more invested, not less, so this one is worth
              keeping rather than deduping away. Distinct cta_surface so GA4 can
              tell us which placement actually earns the install. */}
          <IngredientCheckerInstallAsk
            resultCount={results.length}
            highCount={highCount}
            scanCount={scanCount}
            placement="footer"
          />
        </section>
      )}

      {/* HOW IT WORKS (always visible) */}
      <section className="content py-10">
        <div className="border-t border-ink/10 pt-8">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">Methodology</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-3">
            What this tool checks
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We match your ingredient list against 25+ known microplastic polymers, silicones,
            PEG variants, acrylate / vinyl polymers, and plasticisers commonly used in
            cosmetics and personal care. The full list:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
            {FLAGS.map((flag) => (
              <li key={flag.name} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-forest" />
                <span>
                  <span className="font-semibold">{flag.name}</span>
                  <span className="text-gray-500"> · {flag.category}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 leading-relaxed mt-5">
            The tool is conservative: borderline ingredients (e.g. dimethicone, low-PEG
            variants) are flagged but classed as medium / low concern. A
            matched name does not determine product safety, concentration,
            formulation, rinse-off versus leave-on use, or bodily exposure.
            This is label-text matching, not laboratory analysis.
          </p>
        </div>
      </section>

      {/* RELATED ARTICLES */}
      <section className="content pb-16">
        <h2 className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-4">Read more</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <li>
            <Link href="/blog/worst-microplastic-ingredients" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest font-medium">
              10 Worst Ingredients for Microplastics →
            </Link>
          </li>
          <li>
            <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest font-medium">
              Microplastics in Makeup and Skincare →
            </Link>
          </li>
          <li>
            <Link href="/blog/microplastics-in-cosmetics" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest font-medium">
              Microplastics in Cosmetics: Deep Dive →
            </Link>
          </li>
          <li>
            <Link href="/microplastic-free-products" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest font-medium">
              Lower-Contact Product Guides →
            </Link>
          </li>
        </ul>
      </section>
    </div>
  )
}
